import { Link } from 'react-router'

import { CompanyAccountActivityStatus } from './CompanyAccountActivityStatus'
import { CompanyBatteryEligibility } from './CompanyBatteryEligibility'
import { CompanyMonitoringStatus } from './CompanyMonitoringStatus'
import { CompanyType } from './CompanyType'
import { Integration } from './Integration'
import { Model } from './Model'
import { User } from './User'
import { WarrantyMatchMethod } from './WarrantyMatchMethod'
import { WarrantyProductType } from './WarrantyProductType'
import { CompanyMonitoringStatusEnum, ProductRateTypeEnum, UserRoleEnum, WarrantyProductTypeEnum } from '@/enums'
import { api, companies, Options, Params, Payload, ResponseStatusType } from '@/services'
import {
    AccountManagerDataType,
    CompanyActivityDataType,
    CompanyDataType,
    CompanyEligibleCommissionDataType,
    CompanyProductRatesDataType,
    IdType
} from '@/types'

export class Company extends Model {
    static endpoint = 'api/v1/company'

    raw: CompanyDataType

    unique_id?: string

    name: string

    email: string

    address?: string

    lifetime_eligible_commission: number

    warranty_match_method: WarrantyMatchMethod

    monitoringStatus: CompanyMonitoringStatus

    warranties_lifetime: number

    warranties_this_month: number

    users: User[]

    integrations: Integration[]

    eligible_commission_rates: CompanyEligibleCommissionDataType[]

    activity?: CompanyActivityDataType

    subsidiaries: Company[]

    conglomerate?: Company

    affiliate?: Company

    monthly_sales_quota: number

    battery_eligibility: CompanyBatteryEligibility

    conglomerate_id?: IdType

    create_warranty?: string

    last_warranty_created_at?: Date

    last_warranty_date?: Date

    account_activity_status: CompanyAccountActivityStatus

    total_claim_payout_amount: number

    account_manager?: string

    account_manager_email?: string

    partner_start_date?: Date

    type: CompanyType

    product_rates?: CompanyProductRatesDataType

    marketing_video_url?: string

    constructor(data: CompanyDataType) {
        super(data)
        this.raw = data
        this.unique_id = data.unique_id
        this.name = data.name
        this.email = data.email
        this.address = data.address
        this.type = CompanyType.find(data.type)
        this.account_manager = data.account_manager
        this.account_manager_email = data.account_manager_email
        this.total_claim_payout_amount = Model.toNumber(data.total_claim_payout_amount, 0) as number
        this.partner_start_date = Model.toDate(data.partner_start_date)
        this.account_activity_status = CompanyAccountActivityStatus.find(data.account_activity_status)
        this.battery_eligibility = CompanyBatteryEligibility.find(data.battery_eligibility)
        this.lifetime_eligible_commission = data.lifetime_eligible_commission
        this.warranty_match_method = WarrantyMatchMethod.find(data.warranty_match_method)
        this.monitoringStatus = CompanyMonitoringStatus
            .find(this.type.isCertifiedProvider && data.monitoring?.status === CompanyMonitoringStatusEnum.NOT_REQUIRED
                ? CompanyMonitoringStatusEnum.INCOMPLETE
                : data.monitoring?.status)
        this.monitoringStatus.company = this
        this.monitoringStatus.discovered_at = Model.toDate(data.monitoring?.discovered_at)
        this.users = data.users?.map(item => new User(item)) || []
        this.subsidiaries = data.subsidiaries?.map(item => new Company(item)) || []
        this.monthly_sales_quota = Model.toNumber(data.monthly_sales_quota, 0) as number
        this.warranties_lifetime = Model.toNumber(data.warranties_lifetime, 0) as number
        this.warranties_this_month = Model.toNumber(data.warranties_this_month, 0) as number
        this.integrations = data.integrations
            ?.map(item => new Integration(item))
            .sort((a, b) => a.manufacturer.order - b.manufacturer.order) || []
        this.eligible_commission_rates = data.eligible_commission_rates?.sort((a, b) =>
            +new Date(b.effective_date) - +new Date(a.effective_date)) || []

        this.affiliate = data.affiliate ? new Company(data.affiliate) : undefined
        this.conglomerate_id = data.conglomerate_id
        this.conglomerate = data.conglomerate ? new Company(data.conglomerate) : undefined
        this.create_warranty = data.create_warranty
        this.last_warranty_date = Model.toDate(data.last_warranty_date)
        this.last_warranty_created_at = Model.toDate(data.last_warranty_created_at)
        this.activity = data.activity
        this.product_rates = data.product_rates
        this.marketing_video_url = data.marketing_video_url
    }

    get isSubsidiary() {
        return !!this.conglomerate_id
    }

    get solarEdgeIntegration() {
        return this.integrations?.find((item: Integration) => item.manufacturer.isSolaredge)
    }

    get enphaseIntegration() {
        return this.integrations?.find((item: Integration) => item.manufacturer.isEnphase)
    }

    get generacIntegration() {
        return this.integrations?.find((item: Integration) => item.manufacturer.isGenerac)
    }

    get teslaIntegration() {
        return this.integrations?.find((item: Integration) => item.manufacturer.isTesla)
    }

    get supportedIntegrations() {
        return this.integrations.filter(integration => !integration.status.isUnsupported)
    }

    get unsupportedIntegrations() {
        return this.integrations.filter(integration => integration.status.isUnsupported)
    }

    get currentEligibleCommission() {
        return this.eligible_commission_rates.find(item => new Date(item.effective_date) < new Date())
    }

    get eligibleProductTypes() {
        return WarrantyProductType.all.filter(item =>
            this.battery_eligibility.eligibleProductTypes.includes(item.key))
    }

    isEligibleFor(warrantyProductType: WarrantyProductTypeEnum) {
        return this.battery_eligibility.eligibleProductTypes.includes(warrantyProductType)
    }

    get isUniqueIdValid() {
        return this.unique_id?.startsWith('001') && this.unique_id?.length === 18
    }

    get isRateFieldsValid() {
        return this.eligibleProductTypes.every(item =>
            this.product_rates &&
            (!item.hasPowerProduction || this.product_rates[item.powerProductionRateKey as ProductRateTypeEnum]) &&
            (!item.hasStorage || this.product_rates[item.storageRateKey as ProductRateTypeEnum]))
    }

    get isEligibleToSellWarranties() {
        return !this.monitoringStatus.isIncomplete &&
            this.isUniqueIdValid &&
            this.isRateFieldsValid
    }

    static get eligibleCommissionTooltipContent() {
        return <div className="text-sm p-1">
            <p className="font-bold">Commission Calculation</p>
            <ul className="list-disc pl-4 py-2">
                <li>Commission rate: 20% per watt of every warranty sold by your subcontractors.</li>
                <li className="text-yellow-100">
                    For example, if a subcontractor sells system with a 30-year warranty priced at $5,000, the
                    commission would be 0.2 x $5,000 = $1000.
                </li>
                <li>Commission is calculated automatically based on your affiliate profile.</li>
                <li>To receive commission, your subcontractors must have paid their invoices.</li>
            </ul>
            <p className="mt-1">
                Please ensure that your subcontractors have paid their invoices on time to ensure timely payment of
                commissions. If you have any questions, please contact us for further assistance.
            </p>
            <div className="w-full block text-right">
                <Link className="text-orange-700" to="/support/contact">
                    Contact Us
                </Link>
            </div>
        </div>
    }

    updateSingleField(payload: Payload) {
        return this.update({
            name: this.name,
            email: this.email,
            address: this.address,
            monthly_sales_quota: this.monthly_sales_quota,
            policy_rate: this.product_rates?.power_production,
            battery_only_30_year_rate: this.product_rates?.storage_only,
            battery_20_year_rate: this.product_rates?.separate_20_year_storage,
            battery_30_year_rate: this.product_rates?.separate_30_year_storage,
            conglomerate_id: this.conglomerate?.id,
            unique_id: this.unique_id,
            update_policy: 'all',
            battery_eligibility: this.battery_eligibility.key,
            ...payload
        })
    }

    downloadSystemData() {
        return api.companies.downloadSystemData(this.id)
    }

    activities() {
        return api.companies.activities(this.id)
    }

    updateContacts(payload: Payload) {
        return api.companies.updateCompanyUsers(this.id, payload)
    }

    updateEligibleCommissions(payload: Payload) {
        return api.companies.updateEligibleCommissions(this.id, payload)
    }

    static certifiedProviders(params?: Params, options?: Options) {
        return api.companies.certifiedProviders(params, options)
    }

    static updateSelf(payload: Payload, params?: Params, options?: Options) {
        return api.http.put(Company.endpoint, payload, params, options)
    }

    static async autocomplete(params?: Params) {
        const res = await api.companies.autocomplete(params)
        return res.data?.map((item: CompanyDataType) => new Company(item)) || []
    }

    static onlyAffiliates() {
        return Company.autocomplete({ type: UserRoleEnum.AFFILIATE })
    }

    static onlyCertifiedProviders() {
        return Company.autocomplete({ type: UserRoleEnum.CERTIFIED_PROVIDER })
    }

    static accountManagers(): Promise<{
        status: ResponseStatusType
        data: AccountManagerDataType[]
    }> {
        return companies.accountManagers()
    }
}
